// WAW: sticky add-to-cart bar for phones (markup: snippets/waw-sticky-atc.liquid).
// It owns no logic of its own: its button submits Dawn's product form, so the personalization guard and Dawn's
// handler run exactly as for the main button. This file only decides when the bar shows and what the button says.
(() => {
  if (customElements.get('waw-sticky-atc')) return;

  class WawStickyAtc extends HTMLElement {
    connectedCallback() {
      this.mainButton = document.getElementById(this.dataset.buttonId);
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-waw-sticky-label]');
      this.price = this.querySelector('[data-waw-sticky-price]');
      if (!this.mainButton || !this.button) return;

      const productInfo = this.closest('product-info');
      this.sectionId = productInfo?.dataset.section;
      this.personalization = productInfo?.querySelector('waw-personalization') ?? null;
      this.mainOffScreen = false;
      this.typing = false;

      this.onClick = this.onClick.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.render = this.render.bind(this);

      this.button.addEventListener('click', this.onClick);

      // Show only while the real button is out of view.
      this.visibility = new IntersectionObserver(([entry]) => {
        this.mainOffScreen = !entry.isIntersecting;
        this.render();
      });
      this.visibility.observe(this.mainButton);

      // Mirror the real button: sold out, unavailable, loading, disabled.
      this.mirror = new MutationObserver(this.render);
      this.mirror.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });

      // Never sit on top of the keyboard while the customer is typing their personalization.
      if (this.personalization) {
        this.personalization.addEventListener('focusin', this.onFocusIn);
        this.personalization.addEventListener('focusout', this.onFocusOut);
        this.personalization.addEventListener('waw:change', this.render);
      }

      // Shopify adds its preview bar some time after the page loads (to <body> or to <html>), and it can be closed.
      this.bodyWatch = new MutationObserver(this.render);
      this.bodyWatch.observe(document.body, { childList: true });
      this.bodyWatch.observe(document.documentElement, { childList: true });
      window.addEventListener('resize', this.render);
      this.lateChecks = [1000, 3000].map((delay) => setTimeout(this.render, delay));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (this.sectionId && event.data?.sectionId !== this.sectionId) return;
          // The server already formatted the new price in its copy of this bar.
          const fresh = event.data?.html?.querySelector('[data-waw-sticky-price]');
          if (fresh && this.price) this.price.textContent = fresh.textContent;
        });
      }

      this.render();
    }

    disconnectedCallback() {
      this.visibility?.disconnect();
      this.mirror?.disconnect();
      this.bodyWatch?.disconnect();
      this.lateChecks?.forEach(clearTimeout);
      window.removeEventListener('resize', this.render);
      this.unsubscribeVariant?.();
      this.button?.removeEventListener('click', this.onClick);
      this.personalization?.removeEventListener('focusin', this.onFocusIn);
      this.personalization?.removeEventListener('focusout', this.onFocusOut);
      this.personalization?.removeEventListener('waw:change', this.render);
    }

    /** First required personalization field that is still empty, if any. */
    get firstEmpty() {
      return typeof this.personalization?.firstIncomplete === 'function' ? this.personalization.firstIncomplete() : null;
    }

    onClick(event) {
      const empty = this.firstEmpty;
      if (!empty) return; // a plain submit of the product form: same checks as the main button
      event.preventDefault();
      const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      empty.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' });
      empty.focus({ preventScroll: true });
    }

    onFocusIn(event) {
      this.typing = event.target.matches('input:not([type="checkbox"]), textarea, select');
      this.render();
    }

    onFocusOut() {
      this.typing = false;
      this.render();
    }

    /**
     * Theme PREVIEWS only (share links, "Preview" in admin): Shopify pins its own bar, #preview-bar-iframe, to the
     * bottom of the screen - right on top of this one, so the owner never sees it while checking on a phone.
     * Sit above it. The published theme has no such bar, and then the offset is 0.
     */
    clearPreviewBar() {
      let offset = 0;
      // Shopify has used both ids. 2026-09-20: the owner's phone showed the newer one, which the first version of
      // this method did not know, so the bar sat underneath it.
      for (const id of ['PBarNextFrameWrapper', 'preview-bar-iframe']) {
        const frame = document.getElementById(id);
        if (!frame) continue;
        const rect = frame.getBoundingClientRect();
        const style = getComputedStyle(frame);
        const showing = rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
        const atBottom = rect.bottom >= window.innerHeight - 1;
        const height = Math.round(window.innerHeight - rect.top);
        if (showing && atBottom && height <= 160) offset = Math.max(offset, height);
      }
      this.style.setProperty('--waw-sticky-offset', `${offset}px`);
    }

    render() {
      this.clearPreviewBar();
      const unavailable = this.mainButton.disabled;
      const busy = this.mainButton.getAttribute('aria-disabled') === 'true';
      const personalize = !unavailable && Boolean(this.firstEmpty);

      this.label.textContent = personalize ? this.dataset.labelPersonalize : this.mainButton.querySelector('span')?.textContent.trim() || this.label.textContent;
      this.button.disabled = unavailable;
      this.button.classList.toggle('loading', busy);
      if (busy) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');

      // Keep it in place while it holds keyboard focus, so focus is never pulled out from under someone.
      const show = this.mainOffScreen && !this.typing;
      if (!show && this.contains(document.activeElement)) return;
      this.hidden = !show;
    }
  }

  customElements.define('waw-sticky-atc', WawStickyAtc);
})();
